import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import { Lesson, Term, getLessonCourtIds, getLessonCoachIds } from '@court-sync/shared';
import { useCurrentAcademy } from '../../hooks/useDataStore';
import { useLessons, useCourts, useCoaches, useTerms } from '../../hooks/useDataStore';
import { Card, CardHeader, CardContent } from '../ui/Card';
import { Button } from '@/components/ui';
import { Badge } from '../ui/Badge';
import { Clock, MapPin, Users, Calendar, ArrowRight } from 'lucide-react';
import { getLessonSlotStylePropsForLesson } from '../../utils/lessonSlotColors';
import { useAgeGroupLookups } from '../../queries/ageGroups';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useFacilityClub } from '@/hooks/useFacilityClub';
import { useFacilityScheduleLayer } from '@/hooks/useFacilityScheduleLayer';
import { useScheduleSourceTypeAccess } from '@/hooks/useScheduleSourceTypeAccess';
import { getScheduleOccurrenceChipProps } from '@/utils/scheduleOccurrenceChipProps';
import type { ScheduleOccurrence } from '@/utils/scheduleOccurrences';
import { formatTime, timeToMinutes } from '@/utils/timeFormat';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type TodaysScheduleItem =
  | { kind: 'lesson'; lesson: Lesson; startTime: string; endTime: string }
  | { kind: 'court_hire'; occurrence: ScheduleOccurrence; startTime: string; endTime: string };

function buildScheduleSummaryText(lessonCount: number, courtHireCount: number): string {
  const parts: string[] = [];
  if (lessonCount > 0) {
    parts.push(`${lessonCount} ${lessonCount === 1 ? 'lesson' : 'lessons'}`);
  }
  if (courtHireCount > 0) {
    parts.push(`${courtHireCount} court ${courtHireCount === 1 ? 'hire' : 'hires'}`);
  }
  if (parts.length === 0) return '0 activities scheduled';
  return `${parts.join(' and ')} scheduled`;
}

export default function TodaysSchedule() {
  const navigate = useNavigate();
  const { currentAcademy } = useCurrentAcademy();
  const academyId = currentAcademy?.id;
  const { currentClub } = useCurrentClub();
  const { facilityClub } = useFacilityClub(currentAcademy);
  const sourceAccess = useScheduleSourceTypeAccess();

  const { lessons: allLessons, isLoading: lessonsLoading } = useLessons(academyId);
  const { courts } = useCourts(academyId);
  const { coaches } = useCoaches(academyId);
  const { terms } = useTerms(academyId);
  const { ageGroupById } = useAgeGroupLookups(academyId);

  const today = useMemo(() => new Date(), []);
  const todayRange = useMemo(
    () => ({
      start: startOfDay(today),
      end: endOfDay(today),
    }),
    [today]
  );

  const facilityClubId = facilityClub?.id ?? currentClub?.id;
  const canShowCourtHire = sourceAccess.canShowCourtHire;

  const facilityLayer = useFacilityScheduleLayer({
    clubId: facilityClubId,
    rangeStart: todayRange.start,
    rangeEnd: todayRange.end,
    includeCourtHire: canShowCourtHire,
    includeClosures: false,
    includeOccupancyLessons: false,
    enabled: canShowCourtHire && !!facilityClubId,
  });

  const { todaysLessons, todaysCourtHire, scheduleItems, loading } = useMemo(() => {
    const dayOfWeek = today.getDay();
    const now = new Date();

    if (!academyId && !canShowCourtHire) {
      return {
        todaysLessons: [] as Lesson[],
        todaysCourtHire: [] as ScheduleOccurrence[],
        scheduleItems: [] as TodaysScheduleItem[],
        loading: false,
      };
    }

    if (lessonsLoading || (canShowCourtHire && facilityLayer.loading)) {
      return {
        todaysLessons: [] as Lesson[],
        todaysCourtHire: [] as ScheduleOccurrence[],
        scheduleItems: [] as TodaysScheduleItem[],
        loading: true,
      };
    }

    const activeTerm = (terms || []).find((term: Term) => {
      if (!term.isActive) return false;
      const startDate = new Date(term.startDate);
      const endDate = new Date(term.endDate);
      return now >= startDate && now <= endDate;
    });

    const filteredLessons = academyId
      ? (allLessons || [])
          .filter((lesson) => {
            if (!lesson.isActive) return false;
            const lessonDays = lesson.daysOfWeek ?? [];
            if (lessonDays.length === 0) return false;
            if (!lessonDays.includes(dayOfWeek)) return false;

            if (lesson.lessonType === 'camp') {
              if (!lesson.startDate || !lesson.endDate) return false;
              const cs = new Date(lesson.startDate);
              cs.setHours(0, 0, 0, 0);
              const ce = new Date(lesson.endDate);
              ce.setHours(23, 59, 59, 999);
              return now >= cs && now <= ce;
            }

            if (!lesson.termId) return true;
            if (!activeTerm) return false;
            return lesson.termId === activeTerm.id;
          })
      : [];

    const filteredCourtHire = canShowCourtHire
      ? facilityLayer.courtHireOccurrences.filter((occurrence) =>
          isSameDay(occurrence.date, today)
        )
      : [];

    const items: TodaysScheduleItem[] = [
      ...filteredLessons.map((lesson) => ({
        kind: 'lesson' as const,
        lesson,
        startTime: lesson.startTime,
        endTime: lesson.endTime,
      })),
      ...filteredCourtHire.map((occurrence) => ({
        kind: 'court_hire' as const,
        occurrence,
        startTime: occurrence.startTime,
        endTime: occurrence.endTime,
      })),
    ].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

    return {
      todaysLessons: filteredLessons,
      todaysCourtHire: filteredCourtHire,
      scheduleItems: items,
      loading: false,
    };
  }, [
    academyId,
    allLessons,
    terms,
    lessonsLoading,
    canShowCourtHire,
    facilityLayer.loading,
    facilityLayer.courtHireOccurrences,
    today,
  ]);

  const getCourtName = (courtId: string) => {
    const court = courts.find((c) => c.id === courtId);
    return court?.name || facilityLayer.clubCourts.find((c) => c.id === courtId)?.name || 'Unknown Court';
  };

  const getCoachName = (coachId: string) => {
    const coach = coaches.find((c) => c.id === coachId);
    return coach ? `${coach.firstName} ${coach.lastName}` : 'Unknown Coach';
  };

  const isUnlimited = (lesson: Lesson) => lesson.maxCapacity === -1;

  const isFull = (lesson: Lesson) => {
    if (isUnlimited(lesson)) return false;
    return (lesson.currentEnrollment || 0) >= lesson.maxCapacity;
  };

  const getAvailableSpots = (lesson: Lesson) => {
    return Math.max(0, lesson.maxCapacity - (lesson.currentEnrollment || 0));
  };

  const summaryText = buildScheduleSummaryText(todaysLessons.length, todaysCourtHire.length);
  const calendarLabel = canShowCourtHire ? 'View Calendar' : 'View All Lessons';

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Today's Schedule
          </h3>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Today's Schedule
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/calendar')}
            className="flex items-center self-start sm:self-auto"
          >
            {calendarLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {DAYS_OF_WEEK[today.getDay()]} - {summaryText}
        </p>
      </CardHeader>
      <CardContent>
        {!academyId && !canShowCourtHire ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">No academy selected</p>
            <p className="text-sm">Set up your academy to see today&apos;s schedule.</p>
          </div>
        ) : scheduleItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">
              {canShowCourtHire
                ? 'No lessons or court hire scheduled for today'
                : 'No lessons scheduled for today'}
            </p>
            <p className="text-sm">
              {canShowCourtHire
                ? 'Create lessons or court hire bookings to see them here.'
                : 'Create lessons to see them here.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/calendar')}
              className="mt-4"
            >
              {calendarLabel}
            </Button>
          </div>
        ) : (
          <div className="max-h-[min(50vh,28rem)] overflow-y-auto overflow-x-hidden pr-1 [scrollbar-gutter:stable]">
            <div className="space-y-4">
              {scheduleItems.map((item) => {
                if (item.kind === 'court_hire') {
                  const { occurrence } = item;
                  const chipProps = getScheduleOccurrenceChipProps(occurrence);
                  return (
                    <div
                      key={`court-hire-${occurrence.lessonId}`}
                      className={`rounded-lg p-4 transition-shadow ${chipProps.className}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <h4 className="font-semibold truncate">{occurrence.lessonName}</h4>
                            <Badge
                              variant="secondary"
                              className="bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 flex-shrink-0"
                            >
                              Court hire
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                              <Clock className="h-4 w-4 mr-2" />
                              <span>
                                {formatTime(item.startTime)} - {formatTime(item.endTime)}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                              <MapPin className="h-4 w-4 mr-2" />
                              <span>{occurrence.courtLabel || '—'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                const { lesson } = item;
                const slotStyleProps = getLessonSlotStylePropsForLesson(lesson, ageGroupById);
                return (
                  <div
                    key={lesson.id}
                    className={`rounded-lg p-4 transition-shadow ${slotStyleProps.className}`}
                    style={slotStyleProps.style}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                            {lesson.name}
                          </h4>
                          {isUnlimited(lesson) ? (
                            <Badge
                              variant="secondary"
                              className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex-shrink-0"
                            >
                              Unlimited
                            </Badge>
                          ) : isFull(lesson) ? (
                            <Badge
                              variant="secondary"
                              className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 flex-shrink-0"
                            >
                              Full
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex-shrink-0"
                            >
                              {getAvailableSpots(lesson)} spots
                            </Badge>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Clock className="h-4 w-4 mr-2" />
                            <span>
                              {formatTime(lesson.startTime)} - {formatTime(lesson.endTime)}
                            </span>
                          </div>

                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <MapPin className="h-4 w-4 mr-2" />
                            <span>
                              {getLessonCourtIds(lesson).map(getCourtName).join(', ') || '—'}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                            <span className="truncate">
                              {getLessonCoachIds(lesson).map(getCoachName).join(', ') || '—'} •{' '}
                              {lesson.currentEnrollment || 0} /{' '}
                              {isUnlimited(lesson) ? '∞' : lesson.maxCapacity} students
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
